import React from 'react';
import { Waves, MapPin, Compass, Utensils, Sun, Sparkles, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getDestination } from '../i18n/destination';

// Long-form, SEO-rich "About Ulcinj & Velika Plaža" section.
// Renders translated content + internal-anchor CTAs for all 7 supported locales.
// NOTE: Uses local plain identifiers (not member expressions) inside .map() to avoid
// the visual-edits Babel plugin stack-overflow bug on member-expression iteration.

const ICON_BY_INDEX = [Waves, MapPin, Compass, Utensils, Sun, Sparkles];

const Card = ({ Icon, heading, body, ctaLabel, anchor, idx }) => (
  <article
    className="group bg-white rounded-2xl p-7 lg:p-8 shadow-sm border border-orange-100/60 hover:shadow-md hover:border-orange-200 transition flex flex-col"
    data-testid={'destination-block-' + idx}
  >
    <div className="flex items-start gap-4 flex-1">
      <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center group-hover:bg-orange-500 group-hover:text-white transition">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{heading}</h3>
        <p className="text-sm md:text-base text-gray-600 leading-relaxed">{body}</p>
      </div>
    </div>
    {ctaLabel && anchor ? (
      <a
        href={anchor}
        className="mt-5 ml-15 pl-15 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:text-orange-700 self-start group/cta"
        data-testid={'destination-cta-' + idx}
        style={{ paddingLeft: '60px' }}
      >
        {ctaLabel}
        <ArrowRight className="w-4 h-4 transition-transform group-hover/cta:translate-x-1" />
      </a>
    ) : null}
  </article>
);

const AboutDestination = () => {
  const { lang } = useLanguage();
  const data = getDestination(lang);
  const sectionLabel = data.sectionLabel;
  const title = data.title;
  const intro = data.intro;
  const blocks = data.blocks;

  // Pre-build the cards array so the JSX never iterates a member-expression.
  const cards = blocks.map(function (block, idx) {
    const heading = block.heading;
    const body = block.body;
    const ctaLabel = block.ctaLabel;
    const anchor = block.anchor;
    const Icon = ICON_BY_INDEX[idx] || Sparkles;
    return (
      <Card
        key={heading}
        Icon={Icon}
        heading={heading}
        body={body}
        ctaLabel={ctaLabel}
        anchor={anchor}
        idx={idx}
      />
    );
  });

  return (
    <section
      id="about-destination"
      className="py-20 px-4 bg-gradient-to-b from-white via-orange-50/40 to-white"
      data-testid="about-destination"
    >
      <div className="container mx-auto max-w-6xl">
        <div className="max-w-3xl mb-14">
          <p className="text-sm uppercase tracking-[0.2em] text-orange-600 font-semibold mb-3">
            {sectionLabel}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
            {title}
          </h2>
          <p className="mt-5 text-base md:text-lg text-gray-600 leading-relaxed">{intro}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">{cards}</div>
      </div>
    </section>
  );
};

export default AboutDestination;
